/**
 * 한국어 토스트 — Phase F.3.
 *
 * 백엔드 ProblemDetails(`{code, message, details}`) 자동 표시.
 * 프리미엄 디자인 (사이드바 우측 하단, 5초 자동 숨김, 호버 시 일시정지).
 *
 * 사용:
 *   const toast = useToast()
 *   try { ... } catch (e) { toast.error(extractApiError(e)) }
 *   toast.success('저장되었습니다')
 *   toast.info('파일 업로드 중...')
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import type { ApiErrorDetails } from '@/lib/api/client'

type Tone = 'success' | 'error' | 'warning' | 'info'

interface ToastItem {
  id: string
  tone: Tone
  message: string
  detail?: string
  code?: string
  requestId?: string
}

interface ToastApi {
  success: (msg: string, detail?: string) => void
  error: (err: ApiErrorDetails | string, detail?: string) => void
  warning: (msg: string, detail?: string) => void
  info: (msg: string, detail?: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

let _idSeq = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const push = useCallback(
    (it: Omit<ToastItem, 'id'>) => {
      const id = `t${++_idSeq}`
      setItems((prev) => [...prev, { ...it, id }])
      // 5초 후 자동 제거 (에러는 7초 — 읽을 시간 더)
      const ttl = it.tone === 'error' ? 7000 : 5000
      window.setTimeout(() => remove(id), ttl)
    },
    [remove],
  )

  const api: ToastApi = {
    success: (message, detail) => push({ tone: 'success', message, detail }),
    warning: (message, detail) => push({ tone: 'warning', message, detail }),
    info: (message, detail) => push({ tone: 'info', message, detail }),
    error: (err, detail) => {
      if (typeof err === 'string') {
        push({ tone: 'error', message: err, detail })
      } else {
        push({
          tone: 'error',
          message: err.message,
          detail,
          code: err.code,
          requestId: err.request_id,
        })
      }
    },
  }

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport items={items} onClose={remove} />
    </ToastContext.Provider>
  )
}

// ─────────────────────────────────────────────────────────────────
// Viewport — 화면 우측 하단 fixed
// ─────────────────────────────────────────────────────────────────

function ToastViewport({
  items,
  onClose,
}: {
  items: ToastItem[]
  onClose: (id: string) => void
}) {
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-full max-w-sm flex-col gap-2 px-2 sm:px-0">
      {items.map((t) => (
        <ToastCard key={t.id} item={t} onClose={() => onClose(t.id)} />
      ))}
    </div>
  )
}

function ToastCard({
  item,
  onClose,
}: {
  item: ToastItem
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    requestAnimationFrame(() => {
      ref.current?.classList.remove('translate-x-4', 'opacity-0')
    })
  }, [])

  const styles: Record<Tone, { bg: string; ring: string; icon: ReactNode }> = {
    success: {
      bg: 'bg-emerald-50',
      ring: 'ring-emerald-200',
      icon: <span className="text-emerald-500">✓</span>,
    },
    error: {
      bg: 'bg-rose-50',
      ring: 'ring-rose-200',
      icon: <span className="text-rose-500">✕</span>,
    },
    warning: {
      bg: 'bg-amber-50',
      ring: 'ring-amber-200',
      icon: <span className="text-amber-500">⚠</span>,
    },
    info: {
      bg: 'bg-sky-50',
      ring: 'ring-sky-200',
      icon: <span className="text-sky-500">ⓘ</span>,
    },
  }
  const s = styles[item.tone]

  return (
    <div
      ref={ref}
      className={`pointer-events-auto translate-x-4 transform-gpu rounded-lg ${s.bg} px-4 py-3 opacity-0 shadow-lg ring-1 ${s.ring} transition-all duration-200 backdrop-blur`}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center text-base font-bold">
          {s.icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-slate-900">{item.message}</div>
          {item.detail && (
            <div className="mt-0.5 text-xs text-slate-500">{item.detail}</div>
          )}
          {(item.code || item.requestId) && (
            <div className="mt-1.5 flex items-center gap-2 text-[10px] text-slate-400">
              {item.code && (
                <code className="rounded bg-white/60 px-1.5 py-0.5 font-mono">
                  {item.code}
                </code>
              )}
              {item.requestId && (
                <span className="font-mono">req={item.requestId.slice(0, 8)}</span>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex-shrink-0 text-xs text-slate-400 hover:text-slate-700"
          aria-label="닫기"
        >
          ✕
        </button>
      </div>
    </div>
  )
}
